import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, ExternalLink } from 'lucide-react';
import { frontendDb as db } from '../../lib/frontend-db';
import type { PosterItem } from '../../lib/frontend-db';
const slugify = (input: string) =>
  (input || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[äÄ]/g, 'ae')
    .replace(/[öÖ]/g, 'oe')
    .replace(/[üÜ]/g, 'ue')
    .replace(/[ß]/g, 'ss')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

const PostersPage = () => {
  const [posters, setPosters] = useState<PosterItem[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [editingPoster, setEditingPoster] = useState<PosterItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageTitle, setPageTitle] = useState('Veranstaltungsplakate');

  useEffect(() => {
    loadPosters();
    loadPageTitle();
  }, []);

  const loadPageTitle = async () => {
    try {
      const settings = await db.getSettings();
      if (settings) {
        setPageTitle((settings as any).posters_title || 'Aktuelles Konzert');
      }
    } catch (error) {
      console.error('Error loading page title:', error);
    }
  };

  const loadPosters = async () => {
    try {
      const posterItems = await db.getPosters();
      setPosters(posterItems);
    } catch (error) {
      console.error('Error loading posters:', error);
      setError('Fehler beim Laden der Poster');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!title || !description || !url) {
      setError('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    // Validate URL
    try {
      new URL(url);
    } catch {
      setError('Bitte geben Sie eine gültige URL ein.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let preview_image = editingPoster?.preview_image || '';

      if (selectedImage) {
        try {
          preview_image = await db.storeImage(selectedImage);
          console.log('✅ Image uploaded successfully:', preview_image);
        } catch (imageError) {
          console.error('❌ Error uploading image:', imageError);
          setError('Fehler beim Hochladen des Bildes: ' + (imageError as Error).message);
          return;
        }
      }

      if (editingPoster) {
        try {
          await db.updatePoster(editingPoster.id, {
            title,
            description,
            url,
            preview_image
          });
          console.log('✅ Poster updated successfully');
        } catch (updateError) {
          console.error('❌ Error updating poster:', updateError);
          setError('Fehler beim Aktualisieren des Posters: ' + (updateError as Error).message);
          return;
        }
      } else {
        try {
          await db.createPoster({
            title,
            description,
            url,
            preview_image
          });
          console.log('✅ Poster created successfully');
        } catch (createError) {
          console.error('❌ Error creating poster:', createError);
          setError('Fehler beim Erstellen des Posters: ' + (createError as Error).message);
          return;
        }
      }

      resetForm();
      window.location.reload();
    } catch (error) {
      console.error('❌ Unexpected error saving poster:', error);
      setError('Unerwarteter Fehler beim Speichern des Posters: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Möchten Sie dieses Poster wirklich löschen?')) {
      return;
    }

    try {
      await db.deletePoster(id);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting poster:', error);
      setError('Fehler beim Löschen des Posters');
    }
  };

  const resetForm = () => {
    setEditingPoster(null);
    setTitle('');
    setDescription('');
    setUrl('');
    setSelectedImage(null);
    setPreviewUrl('');
  };

  if (loading && posters.length === 0) {
    return <div className="animate-pulse">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-[var(--primary-color)]">
          {editingPoster ? 'Poster bearbeiten' : 'Neues Poster erstellen'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Titel</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Beschreibung</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">URL</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
              required
              placeholder="https://"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Vorschaubild (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-1 block w-full"
            />
            {(previewUrl || editingPoster?.preview_image) && (
              <div className="mt-2">
                <img
                  src={previewUrl || editingPoster?.preview_image}
                  alt="Vorschau"
                  className="w-full aspect-[2/3] object-cover rounded"
                />
              </div>
            )}
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-700 text-white py-2 px-4 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Wird gespeichert...' : (editingPoster ? 'Aktualisieren' : 'Erstellen')}
            </button>
            {editingPoster && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
              >
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posters.map((poster) => (
          <div key={poster.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold text-lg">{poster.title}</h3>
                <p className="text-sm text-gray-500">
                  {new Date(poster.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex space-x-2">
                <a
                  href={poster.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-blue-600 hover:text-blue-700"
                  title="Link öffnen"
                >
                  <ExternalLink size={20} />
                </a>
                <button
                  onClick={() => {
                    setEditingPoster(poster);
                    setTitle(poster.title);
                    setDescription(poster.description);
                    setUrl(poster.url);
                    setPreviewUrl(poster.preview_image);
                  }}
                  className="p-1 text-green-700 hover:text-green-800"
                  title="Bearbeiten"
                >
                  <Pencil size={20} />
                </button>
                <button
                  onClick={() => handleDelete(poster.id)}
                  className="p-1 text-red-600 hover:text-red-700"
                  title="Löschen"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
            {poster.preview_image && (
              <img
                src={poster.preview_image}
                alt={poster.title}
                className="w-full aspect-[2/3] object-cover rounded mb-4"
              />
            )}
            <p className="text-gray-600 mb-4">{poster.description}</p>
            <a
              href={poster.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 break-all"
            >
              {poster.url}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PostersPage;

